import React from 'react'
import { useTheme } from '@emotion/react'

import { Picture } from '../../content/Picture/Picture'

import {
    StyledMediaContent,
    StyledMedia,
    StyledCardPlayButton,
    StyledGalleryButton,
    StyledGalleryIcon,
    StyledProviderLogo,
    StyledArticleVideo,
    StyledMediaContainer,
} from '../../cards/CardMedia/StyledMedia.styled'

import {
    FixedRatio,
    ImageSource,
    CardBreakpointRatios,
} from '@news-mono/web-common'
import { colors } from '../../__styling/settings/colors'
import { Product } from '@news-mono/web-common'
import { themedValue } from '../../__styling/themed-value'
import { PlayButton } from '../../buttons/PlayButton/PlayButton'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import {
    GetVideoQueue,
    VideoOptions,
} from '../../content/Video/PlayerInterface'
import { VideoDetails } from '@news-mono/web-common'
import { ArticleVideoProps } from '../../publication/ArticleVideo/ArticleVideo'
import inViewport, { ViewportProps } from '../../__helpers/in-viewport'
import { useFeature } from '@news-mono/web-common'
import {
    getCompatibleBreakpointRatios,
    fixedRatiosToCardBreakpoints,
    doesParticularRatioExist,
} from './image-helpers'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
    PublicationCardItemWithVideo,
    CardEvents,
} from '@news-mono/web-common'
import { useConsentGiven } from '@news-mono/web-common'
import { ConsentLevel } from '@news-mono/web-common'
import { ResponsivePictureSizes } from '../../content/Picture/responsive'
import { PicturePlaceholder } from '../../content/Picture/PicturePlaceholder'
import { ResponsivePictureLayout } from '../../content/Picture/ResponsivePictureLayouts'

export type MediaState =
    | 'hidden'
    | 'edgeToEdge'
    | 'fullHeight'
    | 'heroVideoInline'
    | 'default'
export type MediaMode =
    | MediaState
    | Record<'initial' | 'xs' | 'sm' | 'md', MediaState>

export interface CardMediaProps {
    hasBackground?: boolean
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios

    imageWidths: ResponsivePictureSizes

    imageLayout?: ResponsivePictureLayout
    mediaMode?: MediaMode
    hideMediaIcon?: boolean
    providerLogo?: string
    /**
     * flag to modify card styles if a video will play inline
     */
    willPlayVideoInline?: boolean
    getVideoQueue?: GetVideoQueue
    adUnitPath?: string

    isLarge?: boolean
    item: MaybeLoaded<PublicationCardItem>

    disableImageLazyLoad?: boolean
    // other
    onEvent: (event: CardEvents) => void
    className?: string
    playButtonText?: string
}

export const CardMedia: React.FC<CardMediaProps> = ({
    hasBackground = false,
    isLarge = false,
    willPlayVideoInline = false,
    hideMediaIcon = false,
    disableImageLazyLoad = false,
    mediaMode,
    imageLayout = ResponsivePictureLayout.ObjectFitContain,
    fixedRatio = '4:3',
    adUnitPath = '',
    className,
    item,
    getVideoQueue,
    onEvent,
    imageWidths,
    playButtonText,
}) => {
    const theme = useTheme()
    // Default all image as Edge to Edge for the west
    const mediaModeWithDefault =
        mediaMode ||
        themedValue(theme, {
            thewest: 'edgeToEdge',
            sevennews: 'default',
            perthnow: 'default',
            fallback: 'default',
        })
    const canLoadVideoPlayer = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Advertising | ConsentLevel.Analytics,
    })

    const publicationKind = item.loaded
        ? item.result.publicationKind
        : 'unknown'

    const backgroundColor = themedValue(theme, {
        perthnow: hasBackground ? colors.perthnow.greyBasalt : undefined,
        fallback: undefined,
    })

    if (!item.loaded) {
        return (
            <StyledMedia className={className} mediaMode={mediaMode}>
                <PicturePlaceholder
                    image={undefined}
                    fixedRatio={fixedRatiosToCardBreakpoints(fixedRatio, '4:3')}
                    loadingBackgroundColor={backgroundColor}
                />
            </StyledMedia>
        )
    }

    const cardItem = item.result

    const breakpointRatio: CardBreakpointRatios = Array.isArray(fixedRatio)
        ? { default: fixedRatio }
        : typeof fixedRatio === 'string'
        ? { default: fixedRatio }
        : fixedRatio

    if (theme.kind === 'perthnow' && publicationKind === 'gallery') {
        return (
            <CardMediaImage
                mediaMode={mediaModeWithDefault}
                imageLayout={imageLayout}
                fixedRatios={breakpointRatio}
                image={cardItem.image || theme.fallbackImages}
                className={className}
                disableImageLazyLoad={disableImageLazyLoad}
                imageWidths={imageWidths}
                iconOverlay={
                    <StyledGalleryButton>
                        <StyledGalleryIcon />
                    </StyledGalleryButton>
                }
            />
        )
    }

    if (
        isInlineVideoCompatible({ willPlayVideoInline, fixedRatio }) &&
        isPublicationCardItemWithVideo(cardItem) &&
        canLoadVideoPlayer
    ) {
        return (
            <CardMediaInlineVideo
                cardItem={cardItem}
                adUnitPath={adUnitPath}
                disableImageLazyLoad={disableImageLazyLoad}
                fixedRatios={breakpointRatio}
                getVideoQueue={
                    getVideoQueue ||
                    (() =>
                        Promise.resolve({
                            items: [],
                            type: 'not-playlist-video',
                        }))
                }
                hideMediaIcon={hideMediaIcon}
                imageLayout={imageLayout}
                isLarge={isLarge}
                imageWidths={imageWidths}
                mediaMode={mediaModeWithDefault}
                onEvent={onEvent}
                playButtonText={playButtonText}
            />
        )
    }

    if (isPublicationCardItemWithVideo(cardItem) && canLoadVideoPlayer) {
        return (
            <CardMediaImage
                image={cardItem.image || theme.fallbackImages}
                fixedRatios={breakpointRatio}
                mediaMode={mediaModeWithDefault}
                imageLayout={imageLayout}
                className={className}
                disableImageLazyLoad={disableImageLazyLoad}
                imageWidths={imageWidths}
                iconOverlay={
                    <CardMediaPlayButton
                        hideMediaIcon={hideMediaIcon}
                        isLarge={isLarge}
                        willPlayVideoInline={willPlayVideoInline}
                    />
                }
                providerIcon={
                    !!cardItem.providerLogo && (
                        <StyledProviderLogo
                            src={cardItem.providerLogo}
                            alt=""
                        />
                    )
                }
            />
        )
    }

    return (
        <CardMediaImage
            image={cardItem.image || theme.fallbackImages}
            imageLayout={imageLayout}
            mediaMode={mediaModeWithDefault}
            fixedRatios={breakpointRatio}
            className={className}
            disableImageLazyLoad={disableImageLazyLoad}
            imageWidths={imageWidths}
        />
    )
}
CardMedia.displayName = 'CardMedia'

function isInlineVideoCompatible({
    willPlayVideoInline,
    fixedRatio,
}: {
    willPlayVideoInline: boolean
    fixedRatio: FixedRatio | FixedRatio[] | CardBreakpointRatios
}) {
    if (!willPlayVideoInline) {
        return false
    }
    if (!fixedRatio || !doesParticularRatioExist(fixedRatio, '16:9')) {
        return false
    }

    return true
}

interface CardMediaImageProps {
    image: ImageSource
    mediaMode: MediaMode
    fixedRatios: FixedRatio[] | CardBreakpointRatios
    imageLayout: ResponsivePictureLayout
    imageWidths: ResponsivePictureSizes
    className: string | undefined
    iconOverlay?: false | JSX.Element
    providerIcon?: false | JSX.Element
    disableImageLazyLoad: boolean
}

const CardMediaImage = React.forwardRef<HTMLDivElement, CardMediaImageProps>(
    (
        {
            image,
            fixedRatios,
            className,
            iconOverlay = false,
            providerIcon = false,
            disableImageLazyLoad,
            mediaMode,
            imageLayout,
            imageWidths,
        },
        ref,
    ) => {
        const theme = useTheme()
        const breakpointRatio: CardBreakpointRatios = Array.isArray(fixedRatios)
            ? { default: fixedRatios }
            : fixedRatios

        const imageWithFallbacks: ImageSource = {
            ...image,
            crops: {
                original: image.crops.original,
                '161:229':
                    image.crops['161:229'] ||
                    theme.fallbackImages.crops['161:229'],
                '16:9':
                    image.crops['16:9'] || theme.fallbackImages.crops['16:9'],
                '316:421':
                    image.crops['316:421'] ||
                    theme.fallbackImages.crops['316:421'],
                '4:3': image.crops['4:3'] || theme.fallbackImages.crops['4:3'],
            },
        }

        const fixedRatio = getCompatibleBreakpointRatios(
            image.crops,
            breakpointRatio,
        )

        return (
            <StyledMedia ref={ref} className={className} mediaMode={mediaMode}>
                <StyledMediaContent>
                    {iconOverlay}
                    {providerIcon}
                    <Picture
                        image={imageWithFallbacks}
                        fixedRatio={fixedRatio}
                        imageWidths={imageWidths}
                        // Need to use any here because Picture props are a tagged union based
                        // on this property
                        layout={imageLayout}
                        disableLazyLoad={disableImageLazyLoad}
                    />
                </StyledMediaContent>
            </StyledMedia>
        )
    },
)

interface CardMediaInlineVideoWithLazyLoadVideoPlayerProps {
    cardItem: PublicationCardItemWithVideo
    adUnitPath: string
    mediaMode: MediaMode
    fixedRatios: FixedRatio[] | CardBreakpointRatios
    imageWidths: ResponsivePictureSizes
    getVideoQueue: GetVideoQueue
    onEvent: (event: CardEvents) => void
    className?: string
    imageLayout: ResponsivePictureLayout
    isLarge: boolean
    hideMediaIcon: boolean
    disableImageLazyLoad: boolean
    playButtonText?: string
}

const CardMediaInlineVideoWithLazyLoadVideoPlayer: React.FC<
    CardMediaInlineVideoWithLazyLoadVideoPlayerProps & ViewportProps
> = ({
    inViewport,
    cardItem,
    adUnitPath,
    mediaMode,
    getVideoQueue,
    onEvent,
    className,
    fixedRatios,
    hideMediaIcon,
    isLarge,
    imageLayout,
    disableImageLazyLoad,
    imageWidths,
    innerRef,
    playButtonText,
}) => {
    // this is an inline video
    const theme = useTheme()
    const enableNielsenVideoReporting = useFeature(
        'nielsen-video-events-inline-videos',
    )
    const enableSnowplow = useFeature('snowplow')
    const enablePrebid = useFeature('prebid-video')

    if (
        process.env.NODE_ENV === 'test' ||
        process.env.NODE_ENV === 'vr_test' ||
        inViewport > 20
    ) {
        const { id, heading, accountId, source, videoType } = cardItem.video

        const videoDetails: VideoDetails = {
            id,
            accountId,
            heading,
            posterImage: cardItem.image,
            source,
            videoType,
        }

        const videoOptions: VideoOptions = {
            adRequestMode: 'onload',
            adUnitPath,
            preloadOption: 'none',
            canAutoplayVideos: false,
            compactMode: false, // should be false as its not a sticky video
            enableNielsenVideoReporting,
            enableSnowplow,
            enablePrebid,
            playButtonText,
        }

        const videoProps: ArticleVideoProps = {
            showTitles: true,
            videoDetails,
            autoplayOptions: false,
            autoplayNextOptions: {
                delayDuration: 5,
            },
            videoOptions,
            isHeroVideo: true,
            pauseOverlay: true,
            getVideoQueue: getVideoQueue!,
            isVideoStickyEnabled: false,
            onEvent,
            playButtonText,
        }
        return (
            <StyledMediaContainer mediaMode={mediaMode || 'default'}>
                <StyledArticleVideo {...videoProps} />
            </StyledMediaContainer>
        )
    }

    return (
        <CardMediaImage
            ref={innerRef}
            image={cardItem.image || theme.fallbackImages}
            fixedRatios={fixedRatios}
            mediaMode={mediaMode}
            imageLayout={imageLayout}
            className={className}
            disableImageLazyLoad={disableImageLazyLoad}
            imageWidths={imageWidths}
            iconOverlay={
                <CardMediaPlayButton
                    hideMediaIcon={hideMediaIcon}
                    willPlayVideoInline={true}
                    isLarge={isLarge}
                />
            }
            providerIcon={
                !!cardItem.providerLogo && (
                    <StyledProviderLogo src={cardItem.providerLogo} alt="" />
                )
            }
        />
    )
}

const CardMediaInlineVideo = inViewport<
    CardMediaInlineVideoWithLazyLoadVideoPlayerProps
>(CardMediaInlineVideoWithLazyLoadVideoPlayer)

const CardMediaPlayButton: React.FC<{
    hideMediaIcon: boolean
    willPlayVideoInline: boolean
    isLarge: boolean
}> = ({ hideMediaIcon, willPlayVideoInline, isLarge }) => {
    const theme = useTheme()

    const showPlayButton =
        !hideMediaIcon &&
        (theme.kind !== Product.TheWest || willPlayVideoInline === true)

    if (!showPlayButton) {
        return null
    }

    if (theme.kind === 'sevennews') {
        return <PlayButton isIcon isSmall={isLarge !== true} isCentered />
    }
    return <StyledCardPlayButton />
}
